/*
 * The site's colour themes.
 *
 * A theme is one of four names. The name is what the database stores
 * (kb.SiteSettings.Theme, CHECKed against this same list), what the public
 * bootstrap carries, and what the browser puts on the <html> element as
 * data-theme - the stylesheet does the rest, by redefining the handful of accent
 * tokens under [data-theme='...']. Nothing about colour lives here or on the
 * server: a theme is a name, and the stylesheet is the only place that knows
 * what the name looks like. The swatch below is the one exception, and it is a
 * caption, not a token.
 *
 * WHY FOUR FIXED NAMES AND NOT A COLOUR PICKER. A theme is not one colour but a
 * set - the accent, its hover, its soft tint, the text that sits on it, a
 * legible shade for links, a focus ring - twice over, for light and dark. Those
 * have to be chosen together for contrast, and a picker would hand that job to
 * whoever is holding it. Four curated sets can be checked once; a free colour
 * cannot. The list is a const tuple, so the CHECK constraint, the zod schema, the
 * TypeScript union and the option list are declarations of one list.
 *
 * "Classic" is the appearance the knowledge base shipped with, unchanged: blue.
 * "Amber" is built on #F79410, as asked. "Plum" and "Graphite" round the set out
 * with a distinct hue and a neutral.
 */
/**
 * `satisfies` ties this tuple to the SiteTheme union in types.ts: a name added
 * here without being added there fails the build, and the zod enum built from
 * this tuple therefore accepts exactly the union's members.
 */
export const SITE_THEMES = ['classic', 'amber', 'plum', 'graphite'];
/** What a database row has before anyone chooses: the original blue. */
export const DEFAULT_SITE_THEME = 'classic';
export const SITE_THEME_OPTIONS = [
    {
        id: 'classic',
        label: 'Classic',
        description: 'The blue the knowledge base shipped with.',
        swatch: '#0b5fbe',
    },
    {
        id: 'amber',
        label: 'Amber',
        description: 'Warm orange, built on #F79410.',
        swatch: '#F79410',
    },
    {
        id: 'plum',
        label: 'Plum',
        description: 'Deep violet.',
        swatch: '#6d3fc4',
    },
    {
        id: 'graphite',
        label: 'Graphite',
        description: 'Neutral charcoal; colour is left to the content.',
        swatch: '#3b4655',
    },
];
export function isSiteTheme(value) {
    return SITE_THEMES.includes(value);
}
export function siteThemeLabel(theme) {
    return SITE_THEME_OPTIONS.find((option) => option.id === theme)?.label ?? theme;
}
//# sourceMappingURL=siteThemes.js.map